import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";

// Free, OSM-powered geocoder. No API key required.
// Swap PROVIDER to "google" later (requires REACT_APP_GOOGLE_MAPS_KEY) — see TODO at bottom.
const PROVIDER = "photon";
const PHOTON_URL = "https://photon.komoot.io/api/";
const SYDNEY_LAT = -33.8688;
const SYDNEY_LON = 151.2093;

function formatFeature(f) {
  const p = f.properties || {};
  const street = [p.housenumber, p.street].filter(Boolean).join(" ");
  const place = p.name && p.name !== p.street ? p.name : "";
  const locality = p.city || p.town || p.village || p.suburb;
  const parts = [];
  if (place) parts.push(place);
  if (street && street !== place) parts.push(street);
  if (locality) parts.push(locality);
  if (p.state) parts.push(p.state);
  if (p.country) parts.push(p.country);
  // De-duplicate while preserving order
  const seen = new Set();
  return parts.filter((x) => (seen.has(x) ? false : (seen.add(x), true))).join(", ");
}

function extractLeadingNumber(text) {
  // Matches: "13 ", "13a ", "12/45 ", "12-14 " at the start of the string
  const m = (text || "").match(/^\s*(\d+[a-zA-Z]?(?:\s*[-/]\s*\d+[a-zA-Z]?)?)\s+/);
  return m ? m[1].replace(/\s+/g, "") : null;
}

async function searchPhoton(q) {
  const url = `${PHOTON_URL}?q=${encodeURIComponent(
    q,
  )}&limit=7&lang=en&lat=${SYDNEY_LAT}&lon=${SYDNEY_LON}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("geocoder failed");
  const data = await res.json();
  const all = data.features || [];
  const au = all.filter((f) => f.properties?.country === "Australia");
  return (au.length ? au : all).map((f) => ({
    id: `${f.properties?.osm_type || ""}-${f.properties?.osm_id || Math.random()}`,
    label: formatFeature(f),
    raw: f,
  }));
}

export default function AddressAutocomplete({
  value,
  onChange,
  placeholder,
  testid,
  darkMode = false,
}) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const debounceRef = useRef(null);
  const skipNextSearchRef = useRef(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (value !== query) setQuery(value || "");
  }, [value, query]);

  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }
    clearTimeout(debounceRef.current);
    const q = (query || "").trim();
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results =
          PROVIDER === "photon" ? await searchPhoton(q) : [];
        setSuggestions(results);
        setOpen(results.length > 0);
        setActive(-1);
      } catch {
        setSuggestions([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (s) => {
    let label = s.label;
    const props = s.raw?.properties || {};
    // If suggestion lacks a house number but user typed one, prepend it.
    if (!props.housenumber) {
      const userNum = extractLeadingNumber(query);
      if (userNum && !label.startsWith(userNum)) {
        label = `${userNum} ${label}`;
      }
    }
    skipNextSearchRef.current = true;
    setQuery(label);
    onChange(label);
    setOpen(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      pick(suggestions[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        {!darkMode && (
          <MapPin className="absolute left-3 top-1/2 -tranzinc-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
        )}
        <Input
          data-testid={testid}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
          }}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={
            darkMode
              ? "bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-2 h-auto text-sm text-white placeholder:text-white/40 focus-visible:ring-0 focus-visible:border-amber-300/60 transition-colors"
              : "pl-9 pr-9"
          }
          autoComplete="off"
        />
        {loading && (
          <Loader2 className={`absolute right-${darkMode ? "0" : "3"} top-1/2 -tranzinc-y-1/2 w-4 h-4 ${darkMode ? "text-white/50" : "text-zinc-400"} animate-spin`} />
        )}
      </div>
      {open && suggestions.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-40 mt-1 w-full bg-white rounded-lg border border-zinc-200 shadow-lg overflow-hidden max-h-72 overflow-y-auto"
        >
          {suggestions.map((s, i) => (
            <li key={s.id}>
              <button
                type="button"
                data-testid={`${testid}-option-${i}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(s)}
                className={`w-full text-left px-3 py-2.5 flex items-start gap-2 text-sm transition-colors ${
                  i === active ? "bg-zinc-100" : "hover:bg-zinc-50"
                }`}
              >
                <MapPin className="w-4 h-4 sjc-gold mt-0.5 flex-shrink-0" />
                <span className="text-zinc-800">{s.label}</span>
              </button>
            </li>
          ))}
          <li className="px-3 py-2 text-[11px] text-zinc-400 bg-zinc-50 border-t border-zinc-100">
            Tip: you can edit the address after selecting to add a unit or house number.
          </li>
        </ul>
      )}
    </div>
  );
}

// To switch to Google Places later:
// 1. yarn add @googlemaps/js-api-loader
// 2. Add REACT_APP_GOOGLE_MAPS_KEY to /app/frontend/.env
// 3. Set PROVIDER = "google" above and implement searchGoogle() returning
//    [{id, label, raw}] using the Places Autocomplete service biased to country: ['au'].
